import { RightArrowIcon, TypographyV3 } from '@jouzen/ecom-components';
import CircularProgress from '@mui/material/CircularProgress';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { getJobsByLocation } from '../_api/getJobsByLocation';
import { parseListData } from '../_utils/parseListData';
import type { CareerListProps, Job, JobsData } from './types';

const filters = [
  {
    id: 0,
    value: '',
    text: 'View All',
  },
  {
    id: 1,
    value: 'US',
    text: 'North America',
  },
  {
    id: 2,
    value: 'FI',
    text: 'Europe',
  },
];

interface ListItemProps {
  readonly count: number;
  readonly name: string;
}

const ListItem = ({ count, name }: ListItemProps): JSX.Element => {
  const t = useTranslations();
  return (
    <div className="hidden items-center justify-between md:flex">
      <TypographyV3
        className="text-[1.375rem] lg:text-heading-lg"
        weight="light"
      >
        {name}
      </TypographyV3>
      <div className="items-center md:flex md:gap-x-48 xl:gap-x-96">
        <TypographyV3 variant="body-small" className="mt-2">
          {count} {t('open_roles')}
        </TypographyV3>
        <RightArrowIcon />
      </div>
    </div>
  );
};

const MobileListItem = ({ count, name }: ListItemProps): JSX.Element => {
  const t = useTranslations();
  return (
    <div className="flex items-center justify-between md:hidden">
      <div>
        <TypographyV3
          className="text-[1.375rem] lg:text-heading-lg"
          weight="light"
        >
          {name}
        </TypographyV3>
        <TypographyV3 variant="body-small" className="mt-2">
          {count} {t('open_roles')}
        </TypographyV3>
      </div>
      <RightArrowIcon />
    </div>
  );
};

const Alert = ({ message }: { readonly message: MessageKey }): JSX.Element => {
  const t = useTranslations();
  return (
    <div className="flex justify-center py-24">
      <TypographyV3 variant="body-small">{t(message)}</TypographyV3>
    </div>
  );
};

const CareerList = ({
  serverJobsData,
  setDetailView,
}: CareerListProps): JSX.Element => {
  const [jobsData, setJobsData] = useState<JobsData | undefined>(
    serverJobsData,
  );
  const [filter, setFilter] = useState<string>('');

  useEffect(() => {
    const fetchJobsByLocation = async () => {
      if (!filter) {
        setJobsData(serverJobsData);
      } else {
        try {
          const response = await getJobsByLocation(filter);
          setJobsData(response);
        } catch (error) {
          // log to datadog
          setJobsData(undefined);
        }
      }
    };
    void fetchJobsByLocation();
  }, [filter, serverJobsData]);

  const handleSetDetailView = (dept: string) => {
    const filtered = jobsData?.jobs.filter(
      (job: Job) => job.department === dept,
    );

    setDetailView(dept, filtered);
  };

  const parsedListData = parseListData(jobsData);

  const handleSetFilter = (val: string) => setFilter(val);
  const hasJobsData = jobsData && Object.keys(jobsData).length > 0;
  const hasJobsError = typeof jobsData === 'undefined';
  return (
    <>
      <div className="flex gap-x-4 md:mb-4 md:justify-end md:gap-x-12">
        {filters.map(({ id, text, value }) => (
          <div
            key={`region-${id}`}
            className="flex flex-row-reverse items-center gap-x-4"
          >
            <label className="hidden md:block" htmlFor={`filter-${id}`}>
              {text}
            </label>
            <input
              checked={filter === value}
              className={cx(
                'hidden md:block',
                'size-5 cursor-pointer appearance-none rounded-full transition-all',
                'border-2 border-solid border-sandstone-500',
                'checked:bg-blue-200',
              )}
              data-cy={`radio-${text}`}
              id={`filter-${id}`}
              name="radio"
              onChange={() => handleSetFilter(value)}
              type="radio"
              value={value}
            />
            <button
              className="py-4 md:hidden"
              onClick={() => handleSetFilter(value)}
            >
              <TypographyV3
                Element="span"
                className={cx({
                  'text-blue-200': filter === value,
                  'text-sandstone-500': filter !== value,
                })}
              >
                {text}
              </TypographyV3>
            </button>
          </div>
        ))}
      </div>
      <div className="divide-y divide-sandstone-500 border-b border-t-2 border-sandstone-500">
        {hasJobsError && <Alert message="checkout_validate_params_exception" />}
        {!hasJobsData && !hasJobsError && (
          <Alert message="store_locator_no_results" />
        )}
        <Suspense
          fallback={
            <div className="flex justify-center py-24">
              <CircularProgress />
            </div>
          }
        >
          {parsedListData?.map((dept, index) => (
            <button
              className={cx(
                'relative block w-full py-6 text-left text-sandstone-500',
                'border-l-0 border-r-0 border-solid border-transparent',
              )}
              data-cy={`department-item-${index}`}
              key={`dept-${index}`}
              onClick={() => handleSetDetailView(dept.name)}
            >
              <ListItem name={dept.name} count={dept.count} />
              <MobileListItem name={dept.name} count={dept.count} />
            </button>
          ))}
        </Suspense>
      </div>
    </>
  );
};

export default CareerList;
